import { api } from "@/lib/api";
import {
  MAIL_SIGNATURE_EVENT,
  readStoredMailSignaturePair,
  writeMailSignatureEnabled,
  writeMailSignaturePair,
  type MailSignaturePair,
} from "@/mail/signature";

type ServerSignature = { create?: string; reply?: string };

type ServerSignatures = {
  enabled?: boolean;
  stored?: boolean;
  accounts?: Record<string, ServerSignature>;
};

function emailKey(email: string | undefined): string {
  return (email || "").trim().toLowerCase();
}

function pairFromServer(value: ServerSignature | undefined): MailSignaturePair {
  return {
    create: typeof value?.create === "string" ? value.create : "",
    reply: typeof value?.reply === "string" ? value.reply : "",
  };
}

function hasText(pair: MailSignaturePair): boolean {
  return Boolean(pair.create.trim() || pair.reply.trim());
}

/** Pull account signatures into this browser. Upload a local copy once if the account has none yet. */
export async function syncMailSignatures(accounts: { id: string; email?: string }[]): Promise<void> {
  let remote: ServerSignatures;
  try {
    remote = await api<ServerSignatures>("/api/mail/signatures");
  } catch {
    return;
  }
  const stored = remote.stored === true;
  if (stored && typeof remote.enabled === "boolean") writeMailSignatureEnabled(remote.enabled);
  const remoteAccounts = remote.accounts || {};
  for (const account of accounts) {
    const email = emailKey(account.email);
    if (!email) continue;
    const fromServer = pairFromServer(remoteAccounts[email]);
    const local = readStoredMailSignaturePair(account.id);
    if (stored && hasText(fromServer)) {
      writeMailSignaturePair(account.id, fromServer);
      continue;
    }
    if (hasText(local)) {
      try {
        await api("/api/mail/signatures", {
          method: "PUT",
          body: JSON.stringify({ email, create: local.create, reply: local.reply }),
        });
      } catch {
        /* keep the browser copy until the next save */
      }
    }
  }
  if (!stored) {
    try {
      await api("/api/mail/signatures", {
        method: "PUT",
        body: JSON.stringify({ enabled: true }),
      });
    } catch {
      /* ignore */
    }
  }
  try {
    window.dispatchEvent(new CustomEvent(MAIL_SIGNATURE_EVENT));
  } catch {
    /* node */
  }
}

export async function saveMailSignaturePair(
  accountId: string,
  email: string,
  pair: MailSignaturePair,
): Promise<void> {
  await api("/api/mail/signatures", {
    method: "PUT",
    body: JSON.stringify({
      email: emailKey(email),
      create: pair.create,
      reply: pair.reply,
    }),
  });
  writeMailSignaturePair(accountId, pair);
}

export async function saveMailSignatureEnabled(on: boolean): Promise<void> {
  await api("/api/mail/signatures", {
    method: "PUT",
    body: JSON.stringify({ enabled: on }),
  });
  writeMailSignatureEnabled(on);
}
